import { GeoEventType } from 'features/geoEvents/geoEvent.types';
import { getMatchingRegions } from 'features/geoEvents/geoEvent.utils';
import { GeoEventRegion } from 'features/geoEvents/geoEventRegion.types';
import { attemptAsync } from 'es-toolkit/util';
import { useCallback, useRef } from 'react';
import { UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { LatLng } from 'shared/types';
import { hasRegion, withoutRegion } from './useMarkerEditRegionSync.utils';

type FormValues = {
  regions: GeoEventRegion[];
};

type UseMarkerEditRegionSyncProps<T extends FormValues> = {
  geoEventType: GeoEventType;
  regionOptions: GeoEventRegion[];
  enabled: boolean;
  handleRegionsChange: (
    newRegions: GeoEventRegion[],
    currentRegions: GeoEventRegion[],
  ) => GeoEventRegion[];
  getValues: UseFormGetValues<T>;
  setValue: UseFormSetValue<T>;
};

type UseMarkerEditRegionSync = {
  onEnterEditMode: () => void;
  onStage: (latLng: LatLng) => void;
  onCancel: () => void;
  /**
   * The county under the marker's current position, or null. Includes a county
   * that was already listed, which the hook doesn't add or track for removal.
   * Lets a form rebuild its regions baseline without a second geocode.
   */
  getMarkerRegion: () => GeoEventRegion | null;
};

// keeps track of the county under the staged marker in the regions field. Only the county this hook adds
// is ever removed, counties already on the incident or added by the user are left alone.
export const useMarkerEditRegionSync = <T extends FormValues>(
  props: UseMarkerEditRegionSyncProps<T>,
): UseMarkerEditRegionSync => {
  const {
    geoEventType,
    regionOptions,
    enabled,
    handleRegionsChange,
    getValues,
    setValue,
  } = props;
  const autoAddedRegionRef = useRef<GeoEventRegion | null>(null);
  // The auto-added county when edit mode was entered, restored on cancel
  const sessionStartRegionRef = useRef<GeoEventRegion | null>(null);
  const markerRegionRef = useRef<GeoEventRegion | null>(null);
  const sessionStartMarkerRegionRef = useRef<GeoEventRegion | null>(null);
  const lookupIdRef = useRef(0);

  const syncRegion = useCallback(
    (matchedRegion?: GeoEventRegion) => {
      // @ts-expect-error react-hook-form generic limitation
      const currentRegions: GeoEventRegion[] = getValues('regions');
      let nextRegions = currentRegions;

      const autoAddedRegion = autoAddedRegionRef.current;
      if (autoAddedRegion && autoAddedRegion.id !== matchedRegion?.id) {
        nextRegions = handleRegionsChange(
          withoutRegion(currentRegions, autoAddedRegion.id),
          currentRegions,
        );
        // If the removal was blocked (can happen if evac zones in that county, for example), leave the county
        autoAddedRegionRef.current = null;
      }

      if (matchedRegion && !hasRegion(nextRegions, matchedRegion.id)) {
        nextRegions = [...nextRegions, matchedRegion];
        autoAddedRegionRef.current = matchedRegion;
      }

      if (nextRegions !== currentRegions) {
        // @ts-expect-error react-hook-form generic limitation
        setValue('regions', nextRegions, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    },
    [getValues, handleRegionsChange, setValue],
  );

  const onStage = useCallback(
    async (latLng: LatLng) => {
      if (!enabled) return;

      lookupIdRef.current += 1;
      const lookupId = lookupIdRef.current;

      const [, result] = await attemptAsync(() =>
        getMatchingRegions(latLng.lat, latLng.lng, regionOptions, geoEventType),
      );

      // A newer staged move or a cancel has superseded this
      if (lookupId !== lookupIdRef.current) return;

      const matchedRegion = result?.matchingRegions[0];
      markerRegionRef.current = matchedRegion ?? null;
      syncRegion(matchedRegion);
    },
    [enabled, geoEventType, regionOptions, syncRegion],
  );

  const onEnterEditMode = useCallback(() => {
    sessionStartRegionRef.current = autoAddedRegionRef.current;
    sessionStartMarkerRegionRef.current = markerRegionRef.current;
  }, []);

  const onCancel = useCallback(() => {
    if (!enabled) return;
    lookupIdRef.current += 1;
    markerRegionRef.current = sessionStartMarkerRegionRef.current;
    syncRegion(sessionStartRegionRef.current ?? undefined);
  }, [enabled, syncRegion]);

  const getMarkerRegion = useCallback(() => markerRegionRef.current, []);

  return { onEnterEditMode, onStage, onCancel, getMarkerRegion };
};
